const _baseFiles = import.meta.glob('../components/views/**/*.vue');
const _parse = (path, ref) =>
	path
		.replace(ref, '')
		.replace('.vue', '')
		.split('/')
		.map(el => el.charAt(0).toUpperCase() + el.slice(1))
		.filter(el => el !== 'Index')
		.join('');

const _templateRegistry = new Map();

// Base template registry
Object.entries(_baseFiles).forEach(([path, loader]) => {
	_templateRegistry.set(_parse(path, '../components/views/'), loader);
});

export default defineNuxtPlugin(async nuxtApp => {
	const config = useAppConfig();
	const lang = useLang();
	const router = useRouter();
	const route = useRoute();
	nuxtApp.$appData = {};
	nuxtApp.$appData.templates = _templateRegistry;
	nuxtApp.$host = config.host;
	const currentUrl = router.currentRoute.value.fullPath;

	// Set default language based on host. Only if multidomain is enabled
	if (import.meta.server && config.multiHost?.length) {
		const host = useRequestURL().host;
		nuxtApp.$host = host;
		const item = config.multiHost.find(item => host.includes(item.alias));
		if (item) lang.set(item.lang);
	}

	// Set current language before any API calls
	if (config?.multilanguage) {
		const currentLanguage = lang.getLangFromUrl(route.path);
		lang.set(currentLanguage);
	}

	const labels = useLabels();
	const apiRoutes = useApiRoutes();
	const currency = useCurrency();
	const info = useInfo();
	const {fetchRedirect} = useRedirects();

	// Transfers init data from server to client (making sure crawlers/bots can see the source correctly).
	const _initState = useState('_init', () => null);

	if (import.meta.server) {
		const initData = await useApi('/api/nuxtapi/checkInitData/');

		// get token from cache or generate new one
		if (!initData?.token) {
			try {
				await useApi('/api/nuxtapi/token/');
			} catch (err) {
				throw createError({
					statusCode: err.statusCode,
					statusMessage: `Error ${err.statusCode}`,
				});
			}
		}

		// check if url is in redirects list and redirect if necessary
		const redirect = await fetchRedirect(currentUrl);
		if (redirect) {
			const redirectUrl = redirect.redirect_url_without_domain;
			const isExternal = redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://') || redirectUrl.startsWith('//') || /\.[a-zA-Z0-9]+$/.test(redirectUrl.split('?')[0]);
			return navigateTo(redirectUrl, {
				redirectCode: redirect.redirect_code ? redirect.redirect_code : 301,
				external: isExternal,
			});
		}

		// Load all data for SSR using full routes
		await loadData(initData);

		// Find the single route that matches the current URL
		const {matchRoute, getRoute} = useApiRoutes();
		const matchedPath = matchRoute(null, route.path);
		const matchedRoute = matchedPath ? getRoute(matchedPath) : null;
		const {path: _rp, urlsSet: _rs, ...matchedRouteAttrs} = matchedRoute || {};
		_initState.value = {
			currency: initData?.currency || null,
			labels: initData?.labels || null,
			info: initData?.info || null,
			routes: matchedRoute ? {data: {[matchedPath]: matchedRouteAttrs}} : null,
		};
	} else {
		// Client: restore currency/labels/info/route from payload
		const cachedInit = _initState.value;
		_initState.value = null;

		await loadData(cachedInit || {});
		await apiRoutes.fetchRoutes().catch(() => null);
	}

	async function loadData(initData) {
		const fetchPromises = [];

		if (initData?.currency?.data) {
			currency.setCurrency(initData.currency.data);
		} else {
			fetchPromises.push(currency.fetch());
		}

		if (initData?.labels?.data) {
			labels.set(initData.labels.data);
		} else {
			fetchPromises.push(labels.fetch());
		}

		if (initData?.info?.data) {
			info.set(initData.info.data);
		} else {
			fetchPromises.push(info.fetch());
		}

		if (initData?.routes?.data) {
			apiRoutes.setRoutes(initData.routes.data);
		} else {
			fetchPromises.push(apiRoutes.fetchRoutes());
		}

		// fetch data only if there are no cached data
		if (fetchPromises.length) {
			const results = await Promise.allSettled(fetchPromises);
			if (results?.length) {
				for (const result of results) {
					if (result.status === 'rejected') {
						throw createError({
							statusCode: result.reason?.statusCode ? result.reason.statusCode : 500,
							message: result.reason?.statusMessage ? result.reason.statusMessage : '',
						});
					}
				}
			}
		}
	}
});
